/*
  Component: Header — mobile menu panel
  Responsibility: turn the header's navigation into a panel the hamburger opens, and keep
                  the button's aria-expanded honest.

  The markup renders closed and the stylesheet draws it closed, so there is no state for
  this file to establish — it only wires the button up. That is a change from how it worked
  before: it used to add an .is-enhanced marker that the stylesheet waited for, which meant
  the browser painted the un-collapsed header until this deferred script ran. The fallback
  for a browser that will not run it is assets/kl-header-no-js.css, loaded from a <noscript>
  in layout/theme.liquid, which puts the navigation back in the flow and hides the
  hamburger.

  There is no breakpoint in here. Whether the navigation may collapse at all is a question
  about layout, and the stylesheet answers it — on desktop the hamburger is display: none
  and the navigation is a static row regardless of the state class below. That is also why
  a panel left open on a phone survives a resize without needing to be reset from here.

  Nothing in here sets a layout value: the only thing that changes is a class on the
  header root.
*/

function closeHeaderMenu(headerElement, toggle) {
  headerElement.classList.remove('is-menu-open');
  toggle.setAttribute('aria-expanded', 'false');
}

function initHeader(headerElement) {
  const toggle = headerElement.querySelector('[data-kl-header-toggle]');
  const menu = headerElement.querySelector('[data-kl-header-menu]');

  /*
    The drawer this button actually opens below 1280px. Optional, because a header rendered
    without a menu has no drawer to render — the two checks below both allow for that.
    Which tab is showing inside it is not this file's business; that is
    assets/kl-mobile-menu.js.
  */
  const drawer = headerElement.querySelector('[data-kl-mobile-menu]');
  const drawerClose = headerElement.querySelector('[data-kl-mobile-menu-close]');

  if (!toggle || !menu) return;

  toggle.addEventListener('click', () => {
    const isOpen = headerElement.classList.toggle('is-menu-open');

    toggle.setAttribute('aria-expanded', isOpen);
  });

  if (drawerClose) {
    drawerClose.addEventListener('click', () => {
      closeHeaderMenu(headerElement, toggle);
      toggle.focus();
    });
  }

  /*
    Escape is the expected way out of an overlay, and focus may be anywhere inside the
    panel when it is pressed — hence the document rather than the header. Focus returns to
    the button that opened the panel, so the tab order does not restart at the top.
  */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!headerElement.classList.contains('is-menu-open')) return;

    closeHeaderMenu(headerElement, toggle);
    toggle.focus();
  });

  /*
    A click outside the header closes it. Same reasoning as Escape: the panel covers the
    page, so the first instinct is to tap past it rather than to find the button again.
    No focus move here — the tap has already said where the visitor is going.
  */
  document.addEventListener('click', (event) => {
    if (!headerElement.classList.contains('is-menu-open')) return;
    if (menu.contains(event.target) || toggle.contains(event.target)) return;
    if (drawer && drawer.contains(event.target)) return;

    closeHeaderMenu(headerElement, toggle);
  });
}

document.querySelectorAll('[data-kl-header]').forEach(initHeader);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-header]').forEach(initHeader);
});
